import {codeLanguages} from '../resources/codeLanguages';
import copyToClipboard from 'copy-to-clipboard';
import {Selection, TextSelection} from 'prosemirror-state';
import {
  chainCommands,
  deleteSelection,
  exitCode,
  joinBackward,
  selectNodeBackward,
} from 'prosemirror-commands';
import {redo, undo} from 'prosemirror-history';
import 'fomantic-ui-less/definitions/modules/transition';
import 'fomantic-ui-less/definitions/modules/dropdown';

import CheckBoldSvg from '@mdi/svg/svg/check-bold.svg';
import ContentCopySvg from '@mdi/svg/svg/content-copy.svg';
import MenuDownSvg from '@mdi/svg/svg/menu-down.svg';

const computeChange = (oldVal, newVal) => {
    if (oldVal === newVal) {
        return null;
    }

    let start = 0, oldEnd = oldVal.length, newEnd = newVal.length;

    while (start < oldEnd && oldVal.charCodeAt(start) === newVal.charCodeAt(start)) {
        ++start;
    }

    while (oldEnd > start && newEnd > start && oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)) {
        oldEnd--;
        newEnd--;
    }

    return {
        from: start,
        to: oldEnd,
        text: newVal.slice(start, newEnd)
    }
}

export default class CodeBlockNodeView {
    constructor(node, view, getPos) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.incomingChanges = false;
        this.updating = false;
        const language = this.node.attrs.params || '';

        this.codeDropdownLink = document.createElement('div');
        this.codeDropdownLink.className = 'detail mini ui search dropdown';
        if (this.view.editable) {
            this.codeDropdownLink.innerHTML = `<div class="text">Code</div> <img class="icon-12 ui icon" src="${MenuDownSvg}" alt="">`;

            const codeLanguageKeys = Object.keys(codeLanguages);
            codeLanguageKeys.sort();
            const values = codeLanguageKeys.map(key => ({
              name: codeLanguages[key].text,
              value: key
            }));

            const applyLanguageChange = (text, value) => {
              if (language === value.trim()) {
                return;
              }

              const transaction = this.view.state.tr.setNodeMarkup(this.getPos(), undefined, {
                params: value.trim(),
              });
              this.view.dispatch(transaction);
            }

            $(this.codeDropdownLink).dropdown({
              sortSelect: true,
              values: values,
              action: applyLanguageChange,
              duration: 0,
            });
        } else {
            this.codeDropdownLink.innerHTML = `<div class="text">${codeLanguages.hasOwnProperty(language) ? codeLanguages[language].text : 'Code'}</div>`;
            this.codeDropdownLink.classList.add('disabled');
        }

        const codeLanguage = document.createElement('div');
        codeLanguage.className = 'ui top left attached label';
        codeLanguage.appendChild(this.codeDropdownLink);
        codeLanguage.setAttribute('contenteditable', 'false');

        const copyCodeLink = document.createElement('a');
        copyCodeLink.className = 'detail';
        copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${ContentCopySvg}" alt=""> Copy`;
        copyCodeLink.addEventListener('click', event => {
            event.stopPropagation();
            event.preventDefault();

            if (!this.handleCopyCode(copyCodeLink)) {
                return;
            }
            copyToClipboard(this.node.textContent.trim(), {
                format: 'text/plain'
            });
            copyCodeLink.focus();
        });

        const copyCode = document.createElement('div');
        copyCode.className = 'ui top right attached label';
        copyCode.appendChild(copyCodeLink);
        copyCode.setAttribute('contenteditable', 'false');

        const defaultCodeView = document.createElement('div');
        defaultCodeView.className = 'ui active centered inline loader';

        const codeSegment = document.createElement('div');
        codeSegment.className = 'ui segment code-block';
        codeSegment.appendChild(defaultCodeView);

        this.dom = codeSegment;

        import(/* webpackChunkName: "code-editor" */ '../../Common/codeEditor')
        .then(module => module.codeEditor({
          callbackFn: editor => {
            this.codeEditor = editor;

            this.updateLanguage(language);

            defaultCodeView.replaceWith(this.codeEditor.getWrapperElement());

            codeSegment.appendChild(codeLanguage);
            codeSegment.appendChild(copyCode);

            this.codeEditor.on("beforeChange", () => this.incomingChanges = true);

            this.codeEditor.on("cursorActivity", () => {
              if (!this.updating && !this.incomingChanges) {
                this.forwardSelection();
              }
            });

            this.codeEditor.on("changes", () => {
              if (!this.updating) {
                this.valueChanged()
                this.forwardSelection()
              }
              this.incomingChanges = false
            });

            this.codeEditor.on("focus", () => {
              this.forwardSelection();
              this.codeEditor.setOption("styleActiveLine", {nonEmpty: true});
            });
            this.codeEditor.on("blur", () => this.codeEditor.setOption("styleActiveLine", false));
            this.codeEditor.refresh();

            if (node.content.size === 0) {
              this.codeEditor.focus();
            }
          },
          options: {
            readOnly: !this.view.editable ? 'nocursor' : false,
            value: this.node.textContent,
            extraKeys: this.codeMirrorKeymap(module.CodeMirror)
          }
        })).catch(() => {
            codeSegment.innerHTML = '<div class="ui message error visible">Unable to code editor, please try again.</div>';
        });
    }

    forwardSelection() {
        if (!this.view.editable || !this.codeEditor || !this.codeEditor.hasFocus()) {
            return;
        }

        let state = this.view.state;
        let selection = this.asProseMirrorSelection(state.doc);

        if (!selection.eq(state.selection)) {
            this.view.dispatch(state.tr.setSelection(selection));
        }
    }

    asProseMirrorSelection(doc) {
        if (!this.view.editable || !this.codeEditor) {
            return;
        }

        let offset = this.getPos() + 1;
        let anchor = this.codeEditor.indexFromPos(this.codeEditor.getCursor("anchor")) + offset;
        let head = this.codeEditor.indexFromPos(this.codeEditor.getCursor("head")) + offset;
        return TextSelection.create(doc, anchor, head);
    }

    setSelection(anchor, head) {
        if (!this.view.editable || !this.codeEditor) {
            return;
        }

        this.updating = true;
        this.codeEditor.setSelection(this.codeEditor.posFromIndex(anchor), this.codeEditor.posFromIndex(head));
        this.updating = false;
        this.codeEditor.focus();
    }

    valueChanged() {
        if (!this.view.editable || !this.codeEditor) {
            return;
        }

        let change = computeChange(this.node.textContent, this.codeEditor.getValue());
        if (change) {
            let start = this.getPos() + 1;
            let tr = this.view.state.tr.replaceWith(
                start + change.from, start + change.to,
                change.text ? this.view.state.schema.text(change.text) : null)
            this.view.dispatch(tr);
        }
    }

    codeMirrorKeymap(CodeMirror) {
        const mod = /Mac/.test(navigator.platform) ? "Cmd" : "Ctrl";
        return CodeMirror.normalizeKeyMap({
            Backspace: () => {
                if (this.codeEditor && this.codeEditor.getValue().length === 0 && chainCommands(deleteSelection, joinBackward, selectNodeBackward)(this.view.state, this.view.dispatch)) {
                    this.view.focus();
                } else {
                    CodeMirror.commands.delCharBefore(this.codeEditor);
                }
            },
            Up: () => this.maybeEscape("line", -1, CodeMirror),
            Left: () => this.maybeEscape("char", -1, CodeMirror),
            Down: () => this.maybeEscape("line", 1, CodeMirror),
            Right: () => this.maybeEscape("char", 1, CodeMirror),
            [`${mod}-Enter`]: () => {
                const breakCommands = chainCommands(exitCode, (state, dispatch) => {
                    dispatch(state.tr.replaceSelectionWith(state.schema.nodes.hard_break.create()).scrollIntoView());
                    return true
                })(this.view.state, this.view.dispatch);

                if (breakCommands) {
                    this.view.focus()
                }
            },
            [`${mod}-Z`]: () => undo(this.view.state, this.view.dispatch),
            [`Shift-${mod}-Z`]: () => redo(this.view.state, this.view.dispatch),
            [`${mod}-Y`]: () => redo(this.view.state, this.view.dispatch)
        })
    }

    maybeEscape(unit, dir, CodeMirror) {
        let pos = this.codeEditor.getCursor();

        if (this.codeEditor.somethingSelected() ||
            pos.line !== (dir < 0 ? this.codeEditor.firstLine() : this.codeEditor.lastLine()) ||
            (unit === "char" && pos.ch !== (dir < 0 ? 0 : this.codeEditor.getLine(pos.line).length))) {
            return CodeMirror.Pass;
        }

        this.view.focus();
        let targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize);
        let selection = Selection.near(this.view.state.doc.resolve(targetPos), dir);
        this.view.dispatch(this.view.state.tr.setSelection(selection))
        this.view.focus()
    }

    update(node) {
        if (!this.view.editable || !this.codeEditor || node.type !== this.node.type) {
            return false;
        }

        this.node = node;

        this.updateLanguage(node.attrs.params, true);

        const change = computeChange(this.codeEditor.getValue(), node.textContent);
        if (change) {
            this.updating = true;
            this.codeEditor.replaceRange(change.text, this.codeEditor.posFromIndex(change.from), this.codeEditor.posFromIndex(change.to))
            this.updating = false;
        }

        return true
    }

    selectNode() {
        if (!this.view.editable || !this.codeEditor) {
            return;
        }

        this.codeEditor.focus();
    }

    stopEvent() {
        return true;
    }

    updateLanguage(language, focus = false) {
        if (!this.codeEditor) {
            return;
        }

        const codeLanguage = codeLanguages[language.trim()];

        const dropdownSelectedText = this.codeDropdownLink.querySelector('.text');

        if (codeLanguage) {
            dropdownSelectedText.textContent = codeLanguage.text;

            this.codeDropdownLink.classList.add('loading');

            import(/* webpackChunkName: "code-editor-mode/[request]" */ `codemirror/mode/${codeLanguage.mode}/${codeLanguage.mode}.js`).then(() => {
                this.codeDropdownLink.classList.remove('loading', 'error');
                dropdownSelectedText.textContent = codeLanguage.text;

                $(this.codeDropdownLink).dropdown('set selected', language.trim());

                this.codeEditor.setOption("mode", codeLanguage.mime);
                this.codeEditor.refresh();
                focus && this.codeEditor.focus();
            }).catch(() => {
                this.codeDropdownLink.classList.remove('loading');
                this.codeDropdownLink.classList.add('error');
            });
        } else {
            this.codeDropdownLink.classList.remove('loading', 'error');
            dropdownSelectedText.textContent = 'Select language';

            $(this.codeDropdownLink).dropdown('set selected', language.trim());

            this.codeEditor.setOption("mode", '');
            focus && this.codeEditor.focus();
        }
    }

    handleCopyCode(copyCodeLink) {
        let linkDisabled;
        if (linkDisabled) {
            return false;
        }

        copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${CheckBoldSvg}" alt=""> Copied to clipboard`;
        linkDisabled = true;
        setTimeout(() => {
            copyCodeLink.innerHTML = `<img class="icon-12 ui icon" src="${ContentCopySvg}" alt=""> Copy`;
            linkDisabled = false;
        }, 1250);
        return true;
    }
}